import React, { useMemo, useState } from 'react';
import Popper from '@mui/material/Popper';
import styles from './GstOverview.module.scss';
import { GstOverviewSummary, GstOverviewRow, StateCoveragePerState } from '../../../../store/dashboard/gstOverview/types';
import IndiaCustomMap, { GstCoverageStateData } from '../../components/Graph/components/Indiamap/IndiaCustomMap';

interface StatesMapProps {
    summary: GstOverviewSummary;
    rows: GstOverviewRow[];
}

// Aspect-ratio reference for the India-map SVG projection. The rendered map
// size is governed by the .StatesMapSection / .MapContainer SCSS (rem-based);
// these numbers only fix the projection's viewBox shape (1:1 square) inside
// the GST variant of IndiaCustomMap. rem is not applicable here — the prop
// is a numeric value consumed by react-simple-maps, not a CSS dimension.
const MAP_ASPECT_REFERENCE = 555;

// GST status colour rule (RECUR-26035): Active=green, Inactive=grey,
// anything else=red. Mirrors GstinTable.statusClass — same 3 buckets in
// both surfaces (table first column + India-map hover popover). Display
// value is canonicalised on BE (polus formatGstStatus); FE only buckets.
function stateStatusClass(status: string): string {
    const s = (status || '').toLowerCase().trim();
    if (s === 'active') return styles.StateStatusActive;
    if (s === 'inactive') return styles.StateStatusInactive;
    return styles.StateStatusOther;
}

function StatesMap({ summary, rows }: Readonly<StatesMapProps>) {
    const stateCoverage = summary?.stateCoverage;
    const perState = stateCoverage?.perState ?? [];

    const stateData = useMemo<Record<string, GstCoverageStateData>>(() => {
        const result: Record<string, GstCoverageStateData> = {};
        perState.forEach((s: StateCoveragePerState) => {
            result[s.stateCode] = {
                stateName: s.stateName,
                count: s.gstinCount,
                activeCount: s.activeCount,
                inactiveCount: s.inactiveCount,
            };
        });
        return result;
    }, [stateCoverage]);

    const rowsByStateName = useMemo(() => {
        const map = new Map<string, GstOverviewRow[]>();
        rows.forEach((r) => {
            const key = (r.state || '').toUpperCase();
            if (!key) return;
            const list = map.get(key) || [];
            list.push(r);
            map.set(key, list);
        });
        return map;
    }, [rows]);

    const [hovered, setHovered] = useState<{ stateName: string; anchorEl: SVGElement } | null>(null);

    const handleStateHover = (stateName: string | null, anchorEl: SVGElement | null) => {
        if (stateName && anchorEl) {
            setHovered({ stateName, anchorEl });
        } else {
            setHovered(null);
        }
    };

    const hoveredRows = hovered ? rowsByStateName.get(hovered.stateName.toUpperCase()) || [] : [];
    const connectedRows = hoveredRows.filter((r) => r.connected === true);
    const otherRows = hoveredRows.filter((r) => r.connected !== true);

    return (
        <div className={styles.StatesMapSection}>
            <div className={styles.StatesMapTitleRow}>
                <span className={styles.StatesMapTitle}>States Covered in GST</span>
            </div>
            <div className={styles.StatesCountHeadline}>
                <span className={styles.StatesCountValue}>{stateCoverage?.totalStates ?? 0} States</span>
                <span className={styles.StatesCountSub}>with active GST&apos;s</span>
            </div>
            <div className={styles.MapContainer}>
                <IndiaCustomMap
                    variant="gst-coverage"
                    stateData={stateData}
                    onStateHover={handleStateHover}
                    height={MAP_ASPECT_REFERENCE}
                    width={MAP_ASPECT_REFERENCE}
                    error={false}
                />
            </div>
            {/* MUI Popper (not the shared Tooltip) for three reasons specific to this
                map-hover popover:
                  1. Portal-rendered — the popover would otherwise be clipped by
                     .StatesMapSection / .MapContainer overflow + transform context.
                  2. Anchored to a specific <Geography> SVG element (anchorEl) that
                     react-simple-maps gives us at mouseEnter time — Tooltip doesn't
                     support arbitrary external anchors.
                  3. We need `placement="right-start"` + a custom offset to sit beside
                     the hovered state without overlapping the map, which Tooltip's
                     direction prop can't express. */}
            <Popper
                open={!!hovered && hoveredRows.length > 0}
                anchorEl={hovered?.anchorEl ?? null}
                placement="right-start"
                modifiers={[{ name: 'offset', options: { offset: [8, 8] } }]}
                style={{ zIndex: 1300, pointerEvents: 'none' }}
            >
                {hovered && (
                    <div className={styles.StateHoverCard}>
                        <div className={styles.StateHoverTitle}>{hovered.stateName}</div>
                        {connectedRows.map((r) => (
                            <div className={styles.StateHoverRow} key={`c-${r.gstin}`}>
                                <span className={styles.StateHoverGstin}>{r.gstin}</span>
                                <span className={`${styles.StateStatusPill} ${styles.StateStatusConnected}`}>Connected</span>
                            </div>
                        ))}
                        {connectedRows.length > 0 && otherRows.length > 0 && (
                            <div className={styles.StateHoverDivider}>
                                <span className={styles.StateHoverDividerLabel}>Others</span>
                                <span className={styles.StateHoverDividerLine} />
                            </div>
                        )}
                        {otherRows.map((r) => (
                            <div className={styles.StateHoverRow} key={`o-${r.gstin}`}>
                                <span className={styles.StateHoverGstin}>{r.gstin}</span>
                                <span className={`${styles.StateStatusPill} ${stateStatusClass(r.status)}`}>{r.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Popper>
        </div>
    );
}

export default StatesMap;
